import React, { useState, useEffect, ReactElement } from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { FormItem } from 'common/components/antd/Form/FormItem';
import { Select } from 'common/components/antd/Select/Select';
import { Space } from 'common/components/antd/Space/Space';
import { Table } from 'common/components/antd/Table/Table';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import {
  useGetAuditObjectsHistoryQuery,
  useGetAuditObjectsIdQuery,
  useGetAuditObjectsQuery
} from 'domains/audit/queries/audit.query';
import {
  TAuditObjectsHistoryRes,
  TAuditObjectsIdRes,
  TAuditObjectsRes
} from 'domains/audit/types/audit.types';
import { SFiltersContainer } from './audit.page.styles';
import { EAuditColumnKeys, EAuditFiltersForm } from './enums/audit.enums';
import { getColumns } from './table/audit.columns';
import { TReportingAuditsPageLoaderData } from './types/audit.types';

type TPaginationConfig = {
  page: number;
  pageSize: number;
};

const ReportingAuditPage = (): ReactElement => {
  const loaderData = useLoaderData() as TReportingAuditsPageLoaderData;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [activeObject, setActiveObject] = useState<string>(loaderData.getAuditObjectsData[0]);
  const [auditObjectsHistoryParams, setAuditObjectsHistoryParams] = useState<{
    object: string;
    object_id: string;
  }>({
    object: loaderData.getAuditObjectsData[0],
    object_id: loaderData.getAuditObjectsIdData[0],
  });
  // const [paginationData, setPaginationData] = useState<TPaginationConfig>({
  const [paginationData] = useState<TPaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const {
    data: auditObjectsData,
    isFetching: isAuditObjectsDataLoading,
  } = useGetAuditObjectsQuery(
    {},
    {
      refetchOnMount: false,
    },
  );

  const {
    data: auditObjectsIdData,
    isFetching: isAuditObjectsIdDataLoading,
  } = useGetAuditObjectsIdQuery(
    {
      object_name: activeObject,
    },
    {
      refetchOnMount: false,
    },
  );

  const {
    data: auditObjectsHistoryData,
    isFetching: isAuditObjectsHistoryDataLoading,
  } = useGetAuditObjectsHistoryQuery(
    {
      object: auditObjectsHistoryParams.object,
      object_id: auditObjectsHistoryParams.object_id,
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
    },
    {
      refetchOnMount: false,
    },
  );

  useEffect(() => {
    form.setFieldsValue({
      [EAuditFiltersForm.Object]: activeObject,
      [EAuditFiltersForm.ObjectId]: auditObjectsIdData?.[0],
    });
  }, [activeObject, auditObjectsIdData]);

  const auditObjectsOptions = (auditObjectsData || [])
    .map((item) => ({
      label: item,
      value: item,
    }));

  const auditObjectsIdOptions = (auditObjectsIdData || [])
    .map((item) => ({
      label: item,
      value: item,
    }));

  const onFilterFormValuesChangeHandler = (values: Record<EAuditFiltersForm, string>): void => {
    const { object } = values;

    if (object) {
      setActiveObject(object);
    }
  }

  const onFinishFilterFormHandler = (values: Record<EAuditFiltersForm, string>): void => {
    const { object, object_id } = values;

    setAuditObjectsHistoryParams({ object, object_id });
  };

  const columns = getColumns();

  const tableData = auditObjectsHistoryData?.history || [];

  return (
    <div>
      <SFiltersContainer>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={onFilterFormValuesChangeHandler}
          onFinish={onFinishFilterFormHandler}
        >
          <Space>
            <FormItem
              name={EAuditFiltersForm.Object}
              label={t('reporting.audit.filters.input.object.label')}
            >
              <Select
                style={{ width: 272 }}
                loading={isAuditObjectsDataLoading}
                options={auditObjectsOptions}
              />
            </FormItem>
            <FormItem
              name={EAuditFiltersForm.ObjectId}
              label={t('reporting.audit.filters.input.objectId.label')}
            >
              <Select
                style={{ width: 272 }}
                loading={isAuditObjectsIdDataLoading}
                options={auditObjectsIdOptions}
              />
            </FormItem>
            <Button
              loading={isAuditObjectsHistoryDataLoading}
              htmlType="submit"
              type="primary"
              style={{
                marginTop: 6,
              }}
            >
              {t('reporting.audit.filters.button.filter')}
            </Button>
          </Space>
        </Form>
      </SFiltersContainer>
      <Table
        loading={isAuditObjectsHistoryDataLoading}
        rowKey={EAuditColumnKeys.ID}
        dataSource={tableData}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
}

export const loader = async (): Promise<{
  getAuditObjectsData: string[] | [],
  getAuditObjectsIdData: string[] | [],
  getAuditObjectsHistoryData: TAuditObjectsHistoryRes | {},
}> => {
  try {
    const getAuditObjectsQuery = queryKeysClient[EQueryKeys.AuditQueryKeys].getAuditObjects({});
    const getAuditObjectsData: TAuditObjectsRes =
      queryClient.getQueryData(getAuditObjectsQuery.queryKey) ??
      (await queryClient.fetchQuery(getAuditObjectsQuery));

    const getAuditObjectsIdQuery = queryKeysClient[EQueryKeys.AuditQueryKeys].getAuditObjectsId({
      object_name: getAuditObjectsData[0],
    });
    const getAuditObjectsIdData: TAuditObjectsIdRes =
      queryClient.getQueryData(getAuditObjectsIdQuery.queryKey) ??
      (await queryClient.fetchQuery(getAuditObjectsIdQuery));

    const getAuditObjectsHistoryQuery = queryKeysClient[EQueryKeys.AuditQueryKeys].getAuditObjectsHistory({
      object: getAuditObjectsData[0],
      object_id: getAuditObjectsIdData[0],
      offset: (DEFAULT_PAGE - 1) * DEFAULT_PAGE_SIZE,
      limit: DEFAULT_PAGE_SIZE,
    });
    const getAuditObjectsHistoryData: TAuditObjectsHistoryRes =
      queryClient.getQueryData(getAuditObjectsHistoryQuery.queryKey) ??
      (await queryClient.fetchQuery(getAuditObjectsHistoryQuery));

    return {
      getAuditObjectsData,
      getAuditObjectsIdData,
      getAuditObjectsHistoryData,
    };
  } catch (error) {
    console.error(error);

    return {
      getAuditObjectsData: [],
      getAuditObjectsIdData: [],
      getAuditObjectsHistoryData: {},
    };
  }
};

export default ReportingAuditPage;
